import React, { useEffect, useMemo, useState } from "react";

import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import {
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { SimpleFile } from "assets/material-custom-icons";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useFormatDateHook } from "hooks";
import { pickBy } from "lodash";
import { useDealActions, useDealQueriesSelect, useReportsActions, useReportsQueriesSelector } from "store";
import { DateRangeValue, FlatReportsTableData, IInvestorViewTable } from "store/types";
import { formatNumberWithoutRounding } from "utils";

import { FileType } from "../../../constants";
import { ColumnsSetMenu } from "./ColumnsSetMenu/ColumnsSetMenu";
import { FilesCell } from "./FilesCell";
import { StyledStack } from "./StyledStack";

interface Props {
  selectedDealName: string;
  internalDateRange: DateRangeValue<string>;
}

export const DependentTable: React.FC<Props> = ({ selectedDealName, internalDateRange }) => {
  const { getFlatDataAction } = useReportsActions();
  const { updateQueries } = useDealActions();
  const { action, data: tableData, setData, loading } = useActionHook(getFlatDataAction);
  const descriptionTableInvestorView = useDealQueriesSelect().descriptionTableInvestorView;
  const [pdf, setPdf] = useState<{ id: number; file_url: string; name: string } | null>(null);

  const { currency, exchangeMethod } = useReportsQueriesSelector();
  const { filters } = useDealQueriesSelect();

  useEffect(() => {
    if (!selectedDealName) {
      tableData && setData(undefined);
      return;
    }

    const columns = [
      ...(descriptionTableInvestorView.measures ? descriptionTableInvestorView.measures : []),
      ...(descriptionTableInvestorView.characteristics ? descriptionTableInvestorView.characteristics : [])
    ];
    action({
      dateRange: internalDateRange,
      currency,
      columns: [...columns],
      exchangeMethod,
      groupBy: [["investor"], ["date"]],
      filters: {
        ...pickBy(filters, (value) => value.length > 0),
        deal_name: [selectedDealName]
      },
      calculatedFields: [...(descriptionTableInvestorView.calculatedFields || [])]
    });
  }, [selectedDealName, descriptionTableInvestorView]);

  const mapTitle = (key: string, title: string) => {
    switch (key) {
      case "deal":
        return "Deal Name";
      case "date":
        return "Date";
      case "Round":
        return "Type of Shares";
      default:
        // return capitalize(title);
        return title; // Show what API sends
    }
  };

  const reorderColumns = (
    columns: {
      key: string;
      title: string;
      postfix: string;
    }[]
  ) => {
    const dealColumn = columns.find((col) => col.key === "deal");
    const otherColumns = columns.filter((col) => col.key !== "deal");
    return dealColumn ? [dealColumn, ...otherColumns, { key: "files", title: "Files", postfix: "" }] : columns;
  };

  return (
    <StyledStack sx={{ gap: "8px", minHeight: 276, flexGrow: 1, position: "relative" }}>
      <Typography sx={{ color: "#071F35", fontSize: "18px", lineHeight: "24px", fontWeight: 500 }}>
        Investment Description
      </Typography>
      {!loading && tableData && (
        <ColumnsSetMenu
          onSubmit={(queries) => updateQueries({ descriptionTableInvestorView: queries as IInvestorViewTable })}
          columnsSettings={descriptionTableInvestorView}
          sx={{ position: "absolute", right: "5px", top: "55px", zIndex: 4 }}
        />
      )}
      {loading && <Skeleton variant="rounded" sx={{ flexGrow: 1 }} />}
      {!loading && tableData && (
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid #DBE8F5",
            borderBottom: "none"
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader size="small">
            <TableHead sx={{ whiteSpace: "nowrap" }}>
              <TableRow>
                {reorderColumns(tableData?.columns || []).map(({ key, title }, index) => (
                  <TableCell
                    sx={{
                      ...(index === 0 && {
                        position: "sticky",
                        left: 0,
                        zIndex: 5
                      })
                    }}
                    key={key}
                  >
                    {mapTitle(key, title)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData?.rows.map(({ valueData, files }, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: "red"
                  }}
                >
                  {reorderColumns(tableData.columns).map(({ key, title, postfix }, colIndex) => {
                    if (key === "files") {
                      return files?.length ? (
                        <FilesCell files={files || []} key={key + index} setPdf={setPdf} />
                      ) : (
                        <TableCell sx={{ borderBottom: "1px solid #dbe8f5" }} key={key + index} />
                      );
                    }
                    return (
                      <Cell
                        columnKey={key}
                        colTitle={title}
                        key={key + index}
                        postfix={postfix}
                        rowValueData={valueData}
                        isFirstColumn={colIndex === 0}
                      />
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {pdf && (
        <FileViewer
          file={{ file_url: pdf.file_url, type: FileType.PDF, name: pdf.name }}
          isOpen={Boolean(pdf)}
          onClose={() => setPdf(null)}
        />
      )}
      {!loading && !tableData && (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            borderRadius: "8px",
            bgcolor: "#F6F8FC",
            border: "1px solid #DBE8F5"
          }}
        >
          <ListAltRoundedIcon sx={{ fontSize: "40px", color: "#92A7BC" }} />
          <Typography sx={{ color: "#92A7BC", fontSize: "14px", lineHeight: "16px", fontWeight: 400 }}>
            No Deal Selected
          </Typography>
        </Stack>
      )}
    </StyledStack>
  );
};

const linksCollTitles = ["Agreement", "Certificate"];

interface CellProps {
  columnKey: string;
  colTitle: string;
  postfix: string;
  rowValueData: FlatReportsTableData["rows"][0]["valueData"];
  isFirstColumn?: boolean;
}

const Cell: React.FC<CellProps> = ({ columnKey, colTitle, postfix, rowValueData, isFirstColumn }) => {
  const formatDate = useFormatDateHook();

  const value: string | JSX.Element = useMemo(() => {
    const cellValue = rowValueData[columnKey];

    // Handle undefined/null values
    if (cellValue === null || cellValue === undefined) {
      return "-";
    }

    // Format date
    if (columnKey === "date") {
      return formatDate({ date: cellValue });
    }

    // Check if link (for Agreement/Certificate columns)
    if (linksCollTitles.includes(colTitle) && typeof cellValue === "string" && cellValue.length > 2) {
      return (
        <IconButton
          onClick={() => window.open(cellValue, "_blank", "noopener,noreferrer")}
          size="sm"
          sx={{ ml: "-5px" }}
        >
          <SimpleFile />
        </IconButton>
      );
    }

    // Format number
    let result: string | JSX.Element;
    if (typeof cellValue === "number") {
      result = formatNumberWithoutRounding(cellValue, 0);
    } else if (typeof cellValue === "string") {
      result = cellValue;
    } else {
      result = String(cellValue);
    }

    // Apply postfix
    if (postfix && postfix === "%") {
      result = `${result}${postfix}`;
    } else if (postfix && postfix !== "%") {
      result = `${postfix}${result}`;
    }

    return result;
  }, [columnKey, colTitle, postfix, rowValueData, formatDate]);

  return (
    <TableCell
      sx={{
        borderBottom: "1px solid #dbe8f5",
        ...(isFirstColumn && {
          backgroundColor: "white",
          position: "sticky",
          left: 0,
          zIndex: 2
        }),
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: "200px"
      }}
    >
      <MaterialTooltip isShow title={value}>
        <>{value}</>
      </MaterialTooltip>
    </TableCell>
  );
};
